/**
 * @description
 * Stream controls component for managing streaming operations.
 * Provides start/stop buttons, batch size selector, and export functionality.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date October 8, 2025
 * @see admin/src/frontend/components/vscode-lens/StreamControls.test.tsx
 */

import { Download, Play, Square, FileJson, FileSpreadsheet } from 'lucide-react';
import type { ExportFormat } from '../../../types/vscode-lens';

interface StreamControlsProps {
  isStreaming: boolean;
  onStart: () => void;
  onStop: () => void;
  onExport: (format: ExportFormat) => void;
  disabled?: boolean;
  hasData?: boolean;
}

/**
 * Stream controls component
 */
export function StreamControls({
  isStreaming,
  onStart,
  onStop,
  onExport,
  disabled,
  hasData = false,
}: StreamControlsProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Load/Stop Button */}
      {!isStreaming ? (
        <button
          onClick={onStart}
          disabled={disabled}
          className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play className="h-4 w-4" />
          Load Data
        </button>
      ) : (
        <button
          onClick={onStop}
          className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
        >
          <Square className="h-4 w-4" />
          Stop Loading
        </button>
      )}

      {/* Export Dropdown */}
      {hasData && (
        <div className="relative">
          <button
            onClick={() => document.getElementById('export-dropdown')?.classList.toggle('hidden')}
            disabled={disabled || isStreaming}
            className="flex items-center gap-2 rounded-md border border-input bg-white px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            Export
          </button>

          {/* Dropdown Menu */}
          <div
            id="export-dropdown"
            className="hidden absolute z-10 mt-1 w-48 rounded-md border border-input bg-white shadow-lg"
          >
            <div className="py-1">
              <button
                onClick={() => {
                  onExport('csv');
                  document.getElementById('export-dropdown')?.classList.add('hidden');
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  onExport('json');
                  document.getElementById('export-dropdown')?.classList.add('hidden');
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50"
              >
                <FileJson className="h-4 w-4" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Text */}
      {!hasData && !isStreaming && (
        <span className="text-sm text-muted-foreground">
          Select a table and configure filters to load data
        </span>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */